import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from '~/timeInit';

import {
  formatDayLabel,
  getDaySlot,
  getMaxPerDay,
  getScheduleDate,
  getScheduleLengthDays,
  getSlotBlockReason,
  getSlotDayDeals,
} from '~/pages/Marketing/AppOnlyFlash/appOnlyFlashCalendar';

import { APP_EXCLUSIVE_BRAND, APP_EXCLUSIVE_SCHEDULE_TYPE } from '~/consts/appExclusive';

import OffersService, { EligibleAppExclusiveOffersResponse, ScheduleRecord } from '~/services/OffersService';

import { appExclusiveScheduleQueryKeys } from './useGetAppExclusiveSchedules';
import { fetchAppExclusiveSlotCheck } from './useGetAppExclusiveSlotCheck';
import { offerQueryKeys } from './useGetOffer';

// Eligible lookups run a few at a time: a month copied to three regions is ~90 of them.
const CHECK_CONCURRENCY = 5;
const SCHEDULE_SEARCH_LIMIT = 500;

export interface AppExclusiveClonePlanParams {
  sourceRegion: string;
  targetRegions: Array<string>;
  /** Sydney calendar days, YYYY-MM-DD, inclusive. */
  from: string;
  to: string;
}

export interface AppExclusiveCloneRow {
  key: string;
  offerId: string;
  offerName: string;
  /** Offer name for the eligible lookup's name search; null when the offer couldn't be loaded. */
  searchName: string | null;
  region: string;
  date: string;
  /** Length of the slot in days (1 = 24 hours, 2 = 48 hours), the same as the source deal. */
  days: number;
  /** Slot to create; set only when the row can be copied. */
  slot: { start: string; end: string } | null;
  reason: string | null;
}

export const appExclusiveCloneQueryKeys = {
  plan: (params: AppExclusiveClonePlanParams) => [...appExclusiveScheduleQueryKeys.all, 'clone-plan', params] as const,
};

async function mapWithConcurrency<T, R>(items: Array<T>, limit: number, fn: (item: T) => Promise<R>) {
  const results: Array<R> = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await fn(items[index]);
    }
  });
  await Promise.all(workers);
  return results;
}

/**
 * Marks copyable rows that would push a target day past the daily limit once the rows before them are
 * copied. Each row's eligible lookup only sees deals already saved, so two source deals on the same day
 * would both pass against a target day that has room for one.
 */
function flagDaysFilledByEarlierCopies(
  checked: Array<{ row: AppExclusiveCloneRow; eligible: EligibleAppExclusiveOffersResponse | null }>,
): Array<AppExclusiveCloneRow> {
  const plannedByRegionDate = new Map<string, number>();
  return checked.map(({ row, eligible }) => {
    if (!row.slot || !eligible) {
      return row;
    }
    const maxPerDay = getMaxPerDay(eligible);
    const days = getSlotDayDeals(eligible);
    const fullDay = days.find(
      ({ date, deals }) => deals.length + (plannedByRegionDate.get(`${row.region}|${date}`) ?? 0) >= maxPerDay,
    );
    if (fullDay) {
      return {
        ...row,
        slot: null,
        reason: `${formatDayLabel(fullDay.date)} would have more than ${maxPerDay} deals with the other copied deals`,
      };
    }
    days.forEach(({ date }) => {
      const key = `${row.region}|${date}`;
      plannedByRegionDate.set(key, (plannedByRegionDate.get(key) ?? 0) + 1);
    });
    return row;
  });
}

/**
 * Upcoming deals starting in `from`..`to` in the source region, each checked against every target
 * region with the same eligible lookup Add deal uses, for the same 24 or 48 hour length. A deal that
 * isn't on sale in a target region, or lands on a full day, is flagged with the reason instead of copied.
 */
export function useGetAppExclusiveClonePlan(params: AppExclusiveClonePlanParams, enabled: boolean) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: appExclusiveCloneQueryKeys.plan(params),
    queryFn: async ({ signal }): Promise<Array<AppExclusiveCloneRow>> => {
      const now = dayjs();
      const sourceRows = await OffersService.searchSchedules(
        {
          type: APP_EXCLUSIVE_SCHEDULE_TYPE,
          brand: APP_EXCLUSIVE_BRAND,
          region: params.sourceRegion,
          start: getDaySlot(params.from).start.toISOString(),
          end: getDaySlot(params.to).end.toISOString(),
          overlaps: true,
          limit: SCHEDULE_SEARCH_LIMIT,
        },
        { signal },
      );
      // Only deals that haven't started: copying a live or ended day would land in the past.
      const upcoming = sourceRows
        .filter((row) => {
          const date = getScheduleDate(row);
          return date >= params.from && date <= params.to && now.isBefore(dayjs(row.start));
        })
        .sort((a, b) => a.start.localeCompare(b.start));

      const offerIds = Array.from(new Set(upcoming.map((row) => row.fk_offer_salesforce_id)));
      const offers = await Promise.all(
        offerIds.map((offerId) =>
          queryClient
            .ensureQueryData({
              queryKey: offerQueryKeys.detail(offerId),
              queryFn: async () => (await OffersService.getOffer(offerId)).result,
            })
            .catch((): App.Offer | undefined => undefined),
        ),
      );
      const namesById = new Map(offerIds.map((offerId, index) => [offerId, offers[index]?.name ?? null]));

      const checks = params.targetRegions.flatMap((region) =>
        upcoming.map((row: ScheduleRecord) => ({ row, region, date: getScheduleDate(row) })),
      );
      const checked = await mapWithConcurrency(checks, CHECK_CONCURRENCY, async ({ row, region, date }) => {
        const offerId = row.fk_offer_salesforce_id;
        const offerName = namesById.get(offerId);
        const days = getScheduleLengthDays(row);
        const base = {
          key: `${region}-${row.id}`,
          offerId,
          offerName: offerName ?? offerId,
          searchName: offerName,
          region,
          date,
          days,
        };
        try {
          const eligible = await fetchAppExclusiveSlotCheck({ region, date, days, offerId, offerName }, { signal });
          const reason = getSlotBlockReason(eligible, offerId, [], now) ?? null;
          const slot = reason ? null : { start: eligible.slot.start, end: eligible.slot.end };
          return { row: { ...base, reason, slot }, eligible };
        } catch (err) {
          const reason = `Could not check: ${err?.message ?? 'unknown error'}`;
          return { row: { ...base, slot: null, reason }, eligible: null };
        }
      });
      return flagDaysFilledByEarlierCopies(checked);
    },
    enabled: enabled && params.targetRegions.length > 0 && !!params.from && !!params.to,
    // A plan is a snapshot for the review step; useCloneAppExclusiveSchedules checks each row again on write.
    staleTime: Infinity,
    gcTime: 0,
  });
}

export interface AppExclusiveCloneResult {
  created: number;
  failed: Array<{ row: AppExclusiveCloneRow; message: string }>;
}

/**
 * Creates the copyable rows a few at a time. Each row is checked again just before it's written: the
 * plan may be minutes old, and svc-offer doesn't enforce the on-sale rule on write. A row that no
 * longer fits, or fails to save, is reported and doesn't stop the rest. Two rows can share a region and
 * day; the plan keeps them within the daily limit, and svc-offer rejects any write that would go over it.
 */
export function useCloneAppExclusiveSchedules() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rows: Array<AppExclusiveCloneRow>): Promise<AppExclusiveCloneResult> => {
      const copyable = rows.filter((row) => row.slot);
      const outcomes = await mapWithConcurrency(copyable, CHECK_CONCURRENCY, async (row) => {
        try {
          const eligible = await fetchAppExclusiveSlotCheck({
            region: row.region,
            date: row.date,
            days: row.days,
            offerId: row.offerId,
            offerName: row.searchName,
          });
          const reason = getSlotBlockReason(eligible, row.offerId);
          if (reason) {
            return { row, message: `No longer fits: ${reason}` };
          }
          await OffersService.createSchedule(
            {
              type: APP_EXCLUSIVE_SCHEDULE_TYPE,
              brand: APP_EXCLUSIVE_BRAND,
              region: row.region,
              start: eligible.slot.start,
              end: eligible.slot.end,
            },
            row.offerId,
          );
          return null;
        } catch (err) {
          return { row, message: err?.message ?? 'Unknown error' };
        }
      });
      const failed = outcomes.filter((outcome) => outcome !== null);
      return { created: copyable.length - failed.length, failed };
    },
    onSettled: (_data, _error, rows) => {
      queryClient.invalidateQueries({ queryKey: appExclusiveScheduleQueryKeys.all });
      new Set(rows.map((row) => row.offerId)).forEach((offerId) =>
        queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) }),
      );
    },
  });
}
